import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { JournalGroupDownloadInterVarsity } from 'donor-elf-web/lib/journal-utils';
import JournalDigestInfo from 'donor-elf-web/lib/models/journal-digest-info';
import { JournalGroupIndexRouteModel } from 'donor-elf-web/routes/funds/all/journal-groups/journal-group/index';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import FileSaver from 'file-saver';
import Papa from 'papaparse';

export default class JournalGroupIndexController extends Controller {
  @service declare formatDateService: FormatDateService;
  @service declare store: Store;

  model!: JournalGroupIndexRouteModel;

  @tracked loading = true;
  @tracked hideFinished = false;
  @tracked controllerIsActive = false;
  @tracked downloading = false;
  @tracked hiddenJournalIds: string[] = [];
  @tracked savingHiddenJournalIds = false;
  @tracked saveError?: any;

  @tracked sortedJournalDigestInfos: JournalDigestInfo[] = [];

  get actionsMenuItems() {
    return [
      {
        label: 'Edit',
        svgJar: 'edit',
        route: 'funds.all.journal-groups.journal-group.edit',
      },
      {
        label: `${this.hideFinished ? 'Show' : 'Hide'} Finished Journals`,
        svgJar: 'info',
        onClick: () => {
          this.hideFinished = !this.hideFinished;
        },
      },
      {
        label: 'Download',
        svgJar: 'download',
        onClick: this.download,
      },
    ];
  }

  get loadedCount() {
    return Math.max(
      this.model.journalDigestInfos.filter((info) => !info.loading).length,
      1,
    );
  }

  @cached
  get visibleJournalDigestInfos() {
    return this.model.journalDigestInfos.filter(
      (info) => !this.hiddenJournalIds.includes(info.journalSlim.id),
    );
  }

  @cached
  get hiddenJournalDigestInfos() {
    return this.model.journalDigestInfos.filter((info) =>
      this.hiddenJournalIds.includes(info.journalSlim.id),
    );
  }

  @action
  templateDidInsert() {
    this.controllerIsActive = true;
    this.hiddenJournalIds =
      this.model.journalGroupUserSetting?.hiddenJournalIds || [];
    this.sortedJournalDigestInfos = [
      ...this.visibleJournalDigestInfos,
      ...this.hiddenJournalDigestInfos,
    ];
    this.loadJournalDigestInfo(this.sortedJournalDigestInfos[0]);
  }

  @action
  templateWillDestroy() {
    this.controllerIsActive = false;
    this.downloading = false;
    this.loading = true;
    this.saveError = undefined;
  }

  async loadJournalDigestInfo(info: JournalDigestInfo) {
    if (this.controllerIsActive) {
      await info.load();
      let infoIndex = this.sortedJournalDigestInfos.indexOf(info);
      let nextInfo = this.sortedJournalDigestInfos[infoIndex + 1];
      if (nextInfo) {
        this.loadJournalDigestInfo(nextInfo);
      } else {
        this.loading = false;
      }
    }
  }

  @action
  toggleShowProjectedGoal(info: JournalDigestInfo) {
    info.showProjectedGoal = !info.showProjectedGoal;
  }

  get totalJournalPledgeAmount() {
    if (this.model.journalDigestInfos.length === 0) {
      return 0;
    }
    let decisionsRequireGift =
      this.model.journalDigestInfos[0].journalInfo!.journal!
        .decisionsRequireGift;
    if (decisionsRequireGift) {
      return this.model.journalDigestInfos.reduce(
        (sum, info) =>
          sum +
          (info.mainGoalAndDecisions?.intervalCumulativeAmounts[
            info.mainGoalAndDecisions.intervalCumulativeAmounts.length - 1
          ]?.withGift || 0),
        0,
      );
    } else {
      return this.model.journalDigestInfos.reduce(
        (sum, info) =>
          sum +
          (info.mainGoalAndDecisions?.intervalCumulativeAmounts[
            info.mainGoalAndDecisions.intervalCumulativeAmounts.length - 1
          ]?.decision || 0),
        0,
      );
    }
  }

  get totalEngages() {
    if (this.model.journalDigestInfos.length === 0) {
      return 0;
    }
    return this.model.journalDigestInfos.reduce(
      (sum, info) => sum + info.reportIntervarsity?.totalCompletedTaskEngagements! || 0,
      0,
    );
  }

  @action
  toggleShowJournal(info: JournalDigestInfo) {
    if (this.hiddenJournalIds.includes(info.journalSlim.id)) {
      this.hiddenJournalIds = this.hiddenJournalIds.filter(
        (id) => id !== info.journalSlim.id,
      );
    } else {
      this.hiddenJournalIds = [...this.hiddenJournalIds, info.journalSlim.id];
    }
    // don't need to save if it's currently saving because it will save again when it's done
    if (!this.savingHiddenJournalIds) {
      this.saveHiddenJournalIds();
    }
  }

  async saveHiddenJournalIds() {
    this.savingHiddenJournalIds = true;
    let hiddenJournalIdsToSave = [...this.hiddenJournalIds];

    this.saveError = undefined;
    try {
      let journalGroupUserSetting =
        this.model.journalGroupUserSetting ||
        this.store.createRecord('all-funds/journal-group-user-setting', {
          journalGroupId: this.model.journalGroup.id,
        });
      journalGroupUserSetting.hiddenJournalIds = hiddenJournalIdsToSave;
      if (hiddenJournalIdsToSave.length === 0) {
        await journalGroupUserSetting.destroyRecord();
        this.model.journalGroupUserSetting = undefined;
      } else {
        await journalGroupUserSetting.save();
        this.model.journalGroupUserSetting = journalGroupUserSetting;
      }

      // if the hiddenJournalIds have changed since the save started, save again
      if (
        hiddenJournalIdsToSave.sort().join() !==
        this.hiddenJournalIds.sort().join()
      ) {
        this.saveHiddenJournalIds();
      }
    } catch (error) {
      this.saveError = error;
    }
    this.savingHiddenJournalIds = false;
  }

  // This is specific for InterVarsity
  @action
  download() {
    if (this.loading) {
      window.alert(
        'Please wait until all the journals have been loaded before downloading the report',
      );
      return;
    }

    let rows = new JournalGroupDownloadInterVarsity(
      this.sortedJournalDigestInfos,
      this.formatDateService,
    ).generateReport();
    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(
      blob,
      `journal-group-${this.model.journalGroup.name}-report.csv`,
      true,
    );
  }
}
